import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Database, 
  Menu,
  X,
  Upload,
  Calendar,
  ArrowRight,
  RefreshCw,
  Loader2,
  Play,
  ListTodo,
  LogOut,
  User,
  Share2
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import ThemeToggle from './ThemeToggle';
import ShareRunModal from './ShareRunModal';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const { token, user, logout, ready } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [shareRunId, setShareRunId] = React.useState<string | null>(null);

  const loadRuns = React.useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const runs = await apiService.getRuns();
      dispatch({ type: 'SET_RUNS', payload: Array.isArray(runs) ? runs : [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  React.useEffect(() => {
    if (!ready) return;
    if (!token) {
      dispatch({ type: 'SET_RUNS', payload: [] });
      return;
    }
    loadRuns();
  }, [token, ready, dispatch, loadRuns]);

  const navigation = [
    { name: 'Product Overview', href: '/overview', icon: Home },
    { name: 'Create Test Scenarios', href: '/create-scenarios', icon: Play },
    { name: 'Create Task Pipeline', href: '/create-pipeline', icon: Database },
    { name: 'Data Workspace', href: '/workspace', icon: Home },
    { name: 'Test Data Lifecycle Manager', href: '/tdm-lifecycle', icon: ListTodo },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    if (location.pathname === '/' || location.pathname === '/overview') return 'Product Overview';
    if (location.pathname === '/create-scenarios') return 'Create Test Scenarios';
    if (location.pathname === '/tdm-lifecycle') return 'Test Data Lifecycle Manager';
    if (location.pathname === '/create-pipeline') return 'Create Task Pipeline';
    if (location.pathname === '/workspace') return 'Data Workspace';
    if (location.pathname === '/runs') return 'All Runs';
    if (location.pathname.startsWith('/run/')) {
      if (location.pathname.includes('/select')) return 'Select Tables';
      if (location.pathname.includes('/generate')) return 'Generate Data';
      return 'Run Details';
    }
    return 'Test Data Management';
  };

  const formatDate = (timestamp: string | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(9, 11);
      const minute = timestamp.substring(11, 13);
      return `${year}-${month}-${day} ${hour}:${minute}`;
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="h-screen flex bg-windows-50 dark:bg-windows-900">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-72' : 'w-16'} bg-white dark:bg-gray-800 border-r border-windows-200 dark:border-gray-700 transition-all duration-300 ease-in-out`}>
        <div className="flex items-center justify-between p-4 border-b border-windows-200 dark:border-gray-700">
          <div className={`${sidebarOpen ? 'block' : 'hidden'} font-semibold text-lg text-windows-900 dark:text-gray-100`}>
            Test Data Management
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-windows-100 transition-colors duration-200"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="mt-4">
          <div className="px-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  title={item.name}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-accent-900/30'
                      : 'text-windows-700 dark:text-gray-300 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20'
                  }`}
                >
                  <Icon size={20} className="mr-3" />
                  {sidebarOpen && <span className="truncate whitespace-nowrap">{item.name}</span>}
                </button>
              );
            })}
          </div>

          {/* Recent Runs */}
          {sidebarOpen && (
            <div className="mt-8 px-4 border-t border-windows-200 pt-4">
              <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-windows-500 dark:text-gray-400 uppercase tracking-wider">
                Previous Tasks
              </h3>
                <button
                  onClick={() => {
                    if (token) loadRuns();
                  }}
                  className="p-1 hover:bg-windows-100 rounded transition-colors"
                  title="Refresh runs"
                >
                  <RefreshCw size={12} className={state.isLoading ? 'animate-spin text-accent-600' : 'text-windows-500'} />
                </button>
              </div>
              {state.isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-accent-600" />
                </div>
              ) : state.runs.length === 0 ? (
                <div className="text-center py-4">
                  <Database className="h-6 w-6 text-windows-300 mx-auto mb-2" />
                  <p className="text-xs text-windows-500">No runs yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {state.runs.map((run) => {
                    // Navigate to flowchart page when clicking a run
                    const navigatePath = `/run/${run.id}`;
                    const hasSyntheticData = run.has_synthetic_data || (run.synthetic_csv_files && run.synthetic_csv_files.length > 0);
                    
                    return (
                    <button
                      key={run.id}
                      onClick={() => navigate(navigatePath)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 border ${
                        state.currentRun?.id === run.id
                          ? 'bg-accent-50 dark:bg-accent-900/30 border-accent-200 dark:border-accent-700 text-accent-700 dark:text-accent-300'
                          : 'bg-windows-50 dark:bg-gray-700/50 border-windows-200 dark:border-gray-600 hover:bg-windows-100 dark:hover:bg-gray-700 hover:border-windows-300 dark:hover:border-gray-500 text-windows-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <Database size={14} className="text-accent-600 flex-shrink-0" />
                          <span className="font-medium text-sm truncate">{run.name}</span>
                        </div>
                        <ArrowRight size={12} className="text-windows-400 flex-shrink-0 ml-2" />
                      </div>
                      
                      <div className="flex items-center space-x-1 text-xs text-windows-500 dark:text-gray-400 mb-2">
                        <Calendar size={10} />
                        <span>{formatDate(run.timestamp)}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                        {(run.has_schema || run.schema) && (
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                            Schema
                          </span>
                        )}
                        {(run.has_input_data || (run.input_csv_files && run.input_csv_files.length > 0)) && (
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                            {run.input_csv_files?.length || 0} tables
                          </span>
                        )}
                        {hasSyntheticData && (
                          <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">
                            Generated
                          </span>
                        )}
                        {(run as any).shared_with_me && (
                          <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs rounded-full">
                            Shared
                          </span>
                        )}
                        </div>
                        {(run as any).owned && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareRunId(run.id);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-accent-300 text-accent-600 bg-white dark:bg-gray-800 hover:bg-accent-50 dark:hover:bg-accent-900/30"
                            title="Share run"
                          >
                            <Share2 size={14} />
                          </button>
                        )}
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Title Bar */}
        <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-windows-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img
              src="/asset/tngrm.jpeg"
              alt="TNGRM logo"
              className="h-10 w-auto object-contain"
            />
            <h1 className="text-xl font-semibold text-windows-900 dark:text-gray-100">
              {getPageTitle()}
            </h1>
          </div>
          
          <div className="flex items-center space-x-2">
            {token && user && (
              <div className="flex items-center gap-2 text-sm text-windows-600 dark:text-gray-400">
                <User size={16} />
                <span className="max-w-[140px] truncate">{user.display_name || user.email}</span>
                <button
                  onClick={() => logout()}
                  className="p-1.5 rounded hover:bg-windows-100 dark:hover:bg-gray-700 transition"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
            {!token && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-accent-600 dark:text-accent-400 hover:underline"
                >
                  Sign in
                </button>
                <span className="text-windows-400">|</span>
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm text-accent-600 dark:text-accent-400 hover:underline"
                >
                  Register
                </button>
              </div>
            )}
            {state.isLoading && (
              <div className="flex items-center space-x-2 text-windows-600 dark:text-gray-400">
                <div className="spinner-windows w-4 h-4"></div>
                <span className="text-sm">Processing...</span>
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-windows-50 dark:bg-gray-900">
          <Outlet />
        </div>

        {/* Status Bar */}
        <div className="px-6 py-2 bg-white dark:bg-gray-800 border-t border-windows-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-windows-600 dark:text-gray-400">
              {state.runs.length} runs available
            </span>
            {state.currentRun && (
              <span className="text-sm text-windows-600 dark:text-gray-400">
                Current: {state.currentRun.name}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Theme Toggle */}
      <ThemeToggle />

      {shareRunId && (() => {
        const run = state.runs.find(r => r.id === shareRunId);
        return run ? (
          <ShareRunModal
            runId={shareRunId}
            runName={run.name}
            onClose={() => setShareRunId(null)}
            onShared={() => {}}
          />
        ) : null;
      })()}
    </div>
  );
};

export default Layout;

